import { useEffect, useState } from 'react';
import { fetchObjectUrl } from '../api/client';

// Renders a person's photo, or an initials placeholder when none is set. The
// API photo endpoint is Bearer-authenticated, so we can't point an <img src> at
// it directly — instead we fetch the bytes as an authenticated blob and render
// the resulting object URL, revoking it on unmount / photo change.
type AvatarSize = 'sm' | 'md' | 'lg';

function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]![0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]![0] ?? '' : '';
  return (first + last).toUpperCase();
}

export function PersonAvatar({
  personId,
  photoPath,
  fullName,
  size = 'md',
}: {
  personId: number;
  photoPath: string | null | undefined;
  fullName: string;
  size?: AvatarSize;
}): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!photoPath) {
      setUrl(null);
      return;
    }
    let alive = true;
    let created: string | null = null;
    void fetchObjectUrl(`/api/persons/${personId}/photo`)
      .then((u) => {
        if (alive) {
          created = u;
          setUrl(u);
        } else {
          URL.revokeObjectURL(u);
        }
      })
      .catch(() => {
        if (alive) setUrl(null);
      });
    return () => {
      alive = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [personId, photoPath]);

  if (url) {
    return (
      <img
        className={`person-avatar person-avatar--${size}`}
        src={url}
        alt={fullName}
      />
    );
  }
  return (
    <span
      className={`person-avatar person-avatar--${size} person-avatar--placeholder`}
      title={fullName}
      aria-hidden="true"
    >
      {initialsOf(fullName)}
    </span>
  );
}
